import React, { useEffect, useMemo, useRef } from 'react';
import { TileLayout } from "@progress/kendo-react-layout";
import { useWindowSize } from "usehooks-ts";
import { Loader } from '@progress/kendo-react-indicators';
import AssetTemplateReadDto, { Groups, Endpoints, Gateways } from '../../../Models/Assets/AssetTemplateReadDto';
import LoadingSpinner from '../../Shared/Components/LoadingSpinner';
import ErrorCard from '../../Shared/Components/Cards/ErrorCard';
import AssetImageComponent from './Images/AssetImageComponent';
import GreasePumpsTableComponent from './Tables/GreasePumpsTableComponent';
import GreasePumpMappingComponent from './Tables/GreasePumpMappingComponent';
import { useAssetConfigurationQueries } from '../Hooks/useAssetConfigurationQueries';
import {
  buildGreasePumpTemplate,
  buildMappedEndpointsByPump,
  getEndpointUniqueKey,
  getGreasePumpColumnsDisplayPosition
} from '../Utilities/AssetGreasePumpUtils';

interface Props {
  selectedTemplate: any;
  groups: Groups[]; // Add groups prop to access Configuration step groups
  greasePumps: Groups[];
  setGreasePumps: React.Dispatch<React.SetStateAction<Groups[]>>;
  endpoints: Endpoints[];
  setEndpoints: React.Dispatch<React.SetStateAction<Endpoints[]>>;
  selectedGateway: { id: string; serialNumber: string };
  gateways: Gateways[];
  selectedGreasePumpId: string | null;
  onGreasePumpSelect: (greasePumpId: string | null) => void;
  onTemplateChange: (updatedTemplate: AssetTemplateReadDto) => void;
  onGreasePumpImageEditStateChange?: (isEditing: boolean) => void;
  isEdit: boolean;
  greasePumpConnectivity?: 'enabled' | 'disabled';
}

const AssetGreasePumpComponent: React.FC<Props> = ({
  selectedTemplate,
  groups, // Add groups parameter
  greasePumps,
  setGreasePumps,
  endpoints,
  setEndpoints,
  selectedGateway,
  gateways,
  selectedGreasePumpId,
  onGreasePumpSelect,
  onTemplateChange,
  onGreasePumpImageEditStateChange,
  isEdit,
  greasePumpConnectivity = 'enabled'
}) => {
  const { height, width } = useWindowSize();
  const containerHeight = height - 270;
  const isSingleColumn = width < 768;
  const rowHeight = 150;

  // Usar hook customizado para queries (reutilizar do AssetConfigurationComponent)
  const {
    templateConfigQuery,
    assetImageQuery,
    unmappedEndpointDevicesQuery,
    requirementsQuery,
    gatewaysQuery,
  } = useAssetConfigurationQueries(isEdit, selectedTemplate, selectedTemplate);

  // Use correct template data depending on mode
  const baseTemplateData = isEdit ? selectedTemplate : templateConfigQuery.data;
  
  // Create computed template data that reflects the current grease pump connectivity setting
  const templateData = useMemo(() => {
    return baseTemplateData ? {
      ...baseTemplateData,
      isGreasePumpConnectivityEnabled: greasePumpConnectivity === 'enabled'
    } : baseTemplateData;
  }, [baseTemplateData, greasePumpConnectivity]);

  const greasePumpIds = useMemo(
    () => new Set((greasePumps || []).map((pump: any) => pump.id).filter(Boolean)),
    [greasePumps]
  );

  const activeEndpoints = useMemo(
    () => endpoints.filter(ep => ep.status === 'Active' || (ep.status === undefined && ep.isEnabled !== false)),
    [endpoints]
  );

  const unassignedEndpoints = useMemo(
    () => activeEndpoints.filter(endpoint => !endpoint.greasePumpId || !greasePumpIds.has(endpoint.greasePumpId)),
    [activeEndpoints, greasePumpIds]
  );

  const assignedEndpoints = useMemo(
    () => activeEndpoints.filter(endpoint => endpoint.greasePumpId === selectedGreasePumpId),
    [activeEndpoints, selectedGreasePumpId]
  );

  // Initialize grease pump selection when template loads and parent has none (preserves selection when navigating between steps)
  useEffect(() => {
    if (!selectedGreasePumpId) {
      const firstGreasePump = templateData?.greasePumps?.[0] || selectedTemplate?.greasePumps?.[0] || greasePumps?.[0];
      if (firstGreasePump?.id) {
        onGreasePumpSelect(firstGreasePump.id);
      }
    }
  }, [templateData?.greasePumps, selectedTemplate?.greasePumps, greasePumps, selectedGreasePumpId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Separate effect for initializing grease pumps to avoid infinite loop
  useEffect(() => {
    // Only initialize grease pumps if they haven't been set yet (preserve user changes)
    if (templateData?.greasePumps && templateData.greasePumps.length > 0 && greasePumps.length === 0) {
      setGreasePumps(templateData.greasePumps);
    } else if (selectedTemplate?.greasePumps && selectedTemplate.greasePumps.length > 0 && greasePumps.length === 0) {
      setGreasePumps(selectedTemplate.greasePumps);
    }
  }, [templateData?.greasePumps?.length, selectedTemplate?.greasePumps?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update the complete template whenever any component changes
  const lastSentTemplateRef = useRef<string | null>(null);
  const lastTemplateIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!templateData) return;
    
    // Reset when template changes (e.g. user switched template)
    if (templateData?.id !== lastTemplateIdRef.current) {
      lastTemplateIdRef.current = templateData?.id;
      lastSentTemplateRef.current = null;
    }
    
    const updatedTemplate = buildGreasePumpTemplate(
      templateData,
      groups,
      greasePumps,
      endpoints,
      gateways,
      greasePumpConnectivity
    );
    
    // Compare with last sent to avoid loop (hasTemplateChanged can always return true
    // due to JSON key order or extra fields in templateData from API)
    const updatedStr = JSON.stringify(updatedTemplate);
    if (lastSentTemplateRef.current !== updatedStr) {
      lastSentTemplateRef.current = updatedStr;
      onTemplateChange?.(updatedTemplate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, greasePumps, endpoints, gateways, templateData?.id, greasePumpConnectivity]);

  const handleGreasePumpSelect = (groupId: string | null) => {
    onGreasePumpSelect(groupId);
  };

  const handleGreasePumpTableSelectionChange = (id: string | null) => {
    onGreasePumpSelect(id);
  };

  const handleMarkersChange = (updatedMarkers: any[]) => {
    const updatedGreasePumps = greasePumps.map(pump => {
      const marker = updatedMarkers.find(m => m.id === pump.id);
      if (marker) {
        return {
          ...pump,
          xPos: marker.position.xPos,
          yPos: marker.position.yPos
        };
      }
      return pump;
    });
    setGreasePumps(updatedGreasePumps);
  };

  const handleGreasePumpsTableChange = (updatedGreasePumps: Groups[]) => {
    const mergedGreasePumps = updatedGreasePumps.map(updatedGreasePump => {
      const existingGreasePump = greasePumps.find(g => g.id === updatedGreasePump.id);
      return {
        ...updatedGreasePump,
        xPos: existingGreasePump?.xPos || updatedGreasePump.xPos,
        yPos: existingGreasePump?.yPos || updatedGreasePump.yPos
      };
    });
    setGreasePumps(mergedGreasePumps);
  };

  const autoAssignedSinglePumpRef = useRef<string | null>(null);

  useEffect(() => {
    if (isEdit || greasePumps.length !== 1 || endpoints.length === 0) {
      return;
    }

    const singlePumpId = greasePumps[0].id;
    if (!singlePumpId) {
      return;
    }

    const autoAssignKey = `${templateData?.id || 'new'}-${singlePumpId}`;
    if (autoAssignedSinglePumpRef.current === autoAssignKey) {
      return;
    }

    if (endpoints.some(endpoint => endpoint.greasePumpId)) {
      autoAssignedSinglePumpRef.current = autoAssignKey;
      return;
    }

    autoAssignedSinglePumpRef.current = autoAssignKey;
    setEndpoints(prevEndpoints =>
      prevEndpoints.map(endpoint => ({
        ...endpoint,
        greasePumpId: singlePumpId
      }))
    );
  }, [isEdit, greasePumps, endpoints, templateData?.id, setEndpoints]);

  useEffect(() => {
    const mappedEndpointsByPump = buildMappedEndpointsByPump(endpoints);

    setGreasePumps(prevGreasePumps => {
      const nextGreasePumps = prevGreasePumps.map((pump: any) => {
        const nextMappedEndpoints = mappedEndpointsByPump[pump.id] || [];
        const currentMappedEndpoints = pump.mappedEndpoints || [];
        const hasChanged =
          currentMappedEndpoints.length !== nextMappedEndpoints.length ||
          currentMappedEndpoints.some((value: string, index: number) => value !== nextMappedEndpoints[index]);

        return hasChanged
          ? { ...pump, mappedEndpoints: nextMappedEndpoints }
          : pump;
      });

      const changed = nextGreasePumps.some((pump, index) => pump !== prevGreasePumps[index]);
      return changed ? nextGreasePumps : prevGreasePumps;
    });
  }, [endpoints, setGreasePumps]);

  const updateEndpointAssignments = React.useCallback((endpointKeys: string[], nextGreasePumpId: string) => {
    if (endpointKeys.length === 0) {
      return;
    }

    const keys = new Set(endpointKeys);
    setEndpoints(prevEndpoints =>
      prevEndpoints.map(endpoint =>
        keys.has(getEndpointUniqueKey(endpoint))
          ? { ...endpoint, greasePumpId: nextGreasePumpId }
          : endpoint
      )
    );
  }, [setEndpoints]);

  const handleAssignEndpoints = React.useCallback((endpointKeys: string[]) => {
    if (!selectedGreasePumpId) {
      return;
    }

    updateEndpointAssignments(endpointKeys, selectedGreasePumpId);
  }, [selectedGreasePumpId, updateEndpointAssignments]);

  const handleUnassignEndpoints = React.useCallback((endpointKeys: string[]) => {
    updateEndpointAssignments(endpointKeys, '');
  }, [updateEndpointAssignments]);



  const tileData = [
    {
      body: (
        <div className="h-full flex flex-col">
          {assetImageQuery.isLoading ? (
            <div className="flex justify-center items-center h-full">
              <Loader type="pulsing" />
            </div>
          ) : assetImageQuery.data?.data ? (
            <AssetImageComponent 
              key={`${templateData?.id}-${templateData?.imageId}-greasePump`}
              assetData={{
                id: templateData?.id || '',
                name: templateData?.name || '',
                description: templateData?.description || '',
                imageId: templateData?.imageId || '',
                isGreasePumpConnectivityEnabled: greasePumpConnectivity === 'enabled',
                endpoints: endpoints, // Use the endpoints prop from parent
                gateways: templateData?.gateways || [],
                groups: groups, // Use the groups prop from parent instead of templateData
              }}
              selectedGroupId={selectedGreasePumpId}
              onMarkerSelect={handleGreasePumpSelect}
              assetImageUrl={assetImageQuery.data.data}
              onMarkersChange={handleMarkersChange}
              templateName={templateData?.name}
              onEditStateChange={onGreasePumpImageEditStateChange}
              // Grease pump mode props
              mode="greasePump"
              greasePumps={greasePumps}
              selectedGreasePumpId={selectedGreasePumpId}
              onGreasePumpSelect={handleGreasePumpSelect}
              onGreasePumpMarkersChange={handleMarkersChange}
            />
          ) : (
            <div className="flex justify-center items-center h-full text-gray-500">
              No image available
            </div>
          )}
        </div>
      ),
      reorderable: false,
    },
    {
      body: (
        <div className="h-full flex flex-col gap-2 overflow-hidden">
          <div className="h-[40%]">
            <GreasePumpsTableComponent
              greasePumps={greasePumps}
              onGreasePumpsChange={handleGreasePumpsTableChange}
              assetData={templateData || undefined}
              onSelectionChange={handleGreasePumpTableSelectionChange}
              selectedGreasePumpId={selectedGreasePumpId}
              gateways={gateways}
            />
          </div>
          <div className="h-[60%] overflow-auto overscroll-contain greasePumpMappingList">
            <GreasePumpMappingComponent 
              groups={groups}
              selectedGreasePumpId={selectedGreasePumpId}
              selectedGreasePumpName={greasePumps.find(pump => pump.id === selectedGreasePumpId)?.name || null}
              unassignedEndpoints={unassignedEndpoints}
              assignedEndpoints={assignedEndpoints}
              onAssignEndpoints={handleAssignEndpoints}
              onUnassignEndpoints={handleUnassignEndpoints}
            />
          </div>
        </div>
      ),
      reorderable: false,
    },
  ];

  const columnsDisplayPosition = getGreasePumpColumnsDisplayPosition(isSingleColumn);

  if (
    templateConfigQuery.isLoading ||
    assetImageQuery.isLoading ||
    templateConfigQuery.isFetching ||
    assetImageQuery.isFetching ||
    unmappedEndpointDevicesQuery.isLoading ||
    unmappedEndpointDevicesQuery.isFetching ||
    requirementsQuery.isLoading ||
    requirementsQuery.isFetching ||
    gatewaysQuery.isLoading ||
    gatewaysQuery.isFetching
  ) {
    return <LoadingSpinner />
  } else if (
    templateConfigQuery.error ||
    assetImageQuery.error ||
    unmappedEndpointDevicesQuery.error ||
    requirementsQuery.error ||
    gatewaysQuery.error
  ) {
    return <ErrorCard />
  }

  return (
    <div className="w-full pt-1 overflow-y-auto overscroll-contain" style={{ height: containerHeight }}>
      <TileLayout
        columns={isSingleColumn ? 1 : 5}
        rowHeight={rowHeight}
        positions={columnsDisplayPosition}
        gap={{ rows: 10, columns: 10 }}
        items={tileData}
        className="w-full dashboard-page-tile"
        style={{ 
          width: '100%',
          height: isSingleColumn ? 'auto' : '100%'
        }}
      />
    </div>
  );
};

export default AssetGreasePumpComponent; 